import { SITE_URL } from "./seo";
import { CONTACT } from "./contactInfo";

const langPrefix = (lang) => (lang === "en" || lang === "es" ? `/${lang}` : "");
const abs = (u) => (!u ? undefined : u.startsWith("http") ? u : SITE_URL + u);
const ORG_ID = `${SITE_URL}/#organization`;
const clean = (obj) => {
  Object.keys(obj).forEach((k) => {
    if (obj[k] === undefined || obj[k] === "" || (Array.isArray(obj[k]) && !obj[k].length)) delete obj[k];
  });
  return obj;
};

// "9°S 70°W" → { latitude: -9, longitude: -70 }
export function parseCoordinates(raw) {
  const m = /(\d+(?:[.,]\d+)?)\s*°\s*([NSns])[,\s]+(\d+(?:[.,]\d+)?)\s*°\s*([EWew])/.exec(raw || "");
  if (!m) return undefined;
  const lat = parseFloat(m[1].replace(",", ".")) * (m[2].toUpperCase() === "S" ? -1 : 1);
  const lon = parseFloat(m[3].replace(",", ".")) * (m[4].toUpperCase() === "W" ? -1 : 1);
  return { "@type": "GeoCoordinates", latitude: lat, longitude: lon };
}

export function organizationNode(info = {}) {
  const hasAddress = info.company_address && !info.company_address.startsWith("[");
  return clean({
    "@type": hasAddress ? "TravelAgency" : "Organization",
    "@id": ORG_ID,
    name: "De Los Cielos",
    url: `${SITE_URL}/`,
    logo: `${SITE_URL}/brand/cream/dlc-profile-512.png`,
    image: `${SITE_URL}/brand/cream/dlc-og-1200x630.png`,
    email: info.privacy_email && !info.privacy_email.startsWith("[") ? info.privacy_email : CONTACT.email,
    telephone: CONTACT.phoneE164,
    vatID: info.company_vat && !info.company_vat.startsWith("[") ? info.company_vat : undefined,
    address: hasAddress ? { "@type": "PostalAddress", streetAddress: info.company_address, addressCountry: "IT" } : undefined,
    sameAs: [CONTACT.instagram, CONTACT.youtube],
  });
}

// Pagina contatti: ContactPage con i recapiti reali (email, telefono, WhatsApp)
export function contactPageNode({ title, description, lang = "it" }) {
  const url = `${SITE_URL}${langPrefix(lang)}/contatti`;
  return clean({
    "@type": "ContactPage",
    "@id": `${url}#page`,
    url,
    name: title,
    description: (description || "").replace(/\s+/g, " ").slice(0, 300),
    inLanguage: lang,
    isPartOf: { "@id": `${SITE_URL}/#website` },
    about: { "@id": ORG_ID },
    mainEntity: {
      "@id": ORG_ID,
      contactPoint: [
        {
          "@type": "ContactPoint",
          contactType: "customer support",
          email: CONTACT.email,
          telephone: CONTACT.phoneE164,
          availableLanguage: ["it", "en", "es"],
          url: CONTACT.whatsapp,
        },
      ],
    },
  });
}

export function webSiteNode(lang = "it") {
  return {
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: `${SITE_URL}/`,
    name: "De Los Cielos",
    inLanguage: lang,
    publisher: { "@id": ORG_ID },
  };
}

export function breadcrumbNode(items, lang = "it") {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: SITE_URL + langPrefix(lang) + it.path,
    })),
  };
}

export function graph(nodes) {
  return { "@context": "https://schema.org", "@graph": nodes.filter(Boolean) };
}

// Spedizione: TouristTrip con prezzo, durata, itinerario e destinazione geolocalizzata
export function tourNodes({ tour, lt, cover, lang = "it", labels = {} }) {
  const url = `${SITE_URL}${langPrefix(lang)}/viaggi/${tour.slug}`;
  const geo = parseCoordinates(tour.coordinates);
  const destination = clean({
    "@type": "TouristDestination",
    "@id": `${url}#destination`,
    name: lt.destination_name || tour.destination_name || lt.location || tour.location,
    address: clean({
      "@type": "PostalAddress",
      addressRegion: lt.location || tour.location,
      addressCountry: lt.country || tour.country,
    }),
    geo,
  });

  const trip = clean({
    "@type": "TouristTrip",
    "@id": `${url}#trip`,
    name: lt.title,
    description: (lt.excerpt || lt.description || "").replace(/\s+/g, " ").slice(0, 500),
    url,
    inLanguage: lang,
    image: (tour.images || []).map(abs).filter(Boolean).slice(0, 6),
    provider: { "@id": ORG_ID },
    arrivalPlace: { "@id": destination["@id"] },
    itinerary: (lt.itinerary || []).length
      ? {
          "@type": "ItemList",
          numberOfItems: lt.itinerary.length,
          itemListElement: lt.itinerary.map((d, i) => clean({
            "@type": "ListItem",
            position: i + 1,
            name: d.title,
            description: (d.description || "").replace(/\s+/g, " ").slice(0, 300),
          })),
        }
      : undefined,
    offers: tour.price
      ? clean({
          "@type": "Offer",
          price: tour.price,
          priceCurrency: tour.currency || "EUR",
          availability: tour.badge === "soldout" ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
          url: `${SITE_URL}${langPrefix(lang)}/candidatura?tour=${tour.slug}`,
          category: labels.offerCategory,
        })
      : undefined,
    additionalProperty: [
      tour.duration_days ? { "@type": "PropertyValue", name: labels.duration || "Durata", value: `${tour.duration_days}` , unitText: labels.days || "giorni" } : null,
      tour.group_max ? { "@type": "PropertyValue", name: labels.group || "Gruppo massimo", value: `${tour.group_max}` } : null,
      tour.difficulty ? { "@type": "PropertyValue", name: labels.difficulty || "Difficoltà", value: tour.difficulty } : null,
      lt.dates_label ? { "@type": "PropertyValue", name: labels.dates || "Partenza", value: lt.dates_label } : null,
    ].filter(Boolean),
  });

  return [trip, destination];
}

// Tour leader: Person legata all'organizzazione
export function leaderNode({ leader, portrait, lang = "it" }) {
  const url = `${SITE_URL}${langPrefix(lang)}/leader/${leader.slug}`;
  return clean({
    "@type": "Person",
    "@id": `${url}#person`,
    name: leader.name,
    url,
    jobTitle: leader.role,
    description: (leader.bio || "").replace(/\s+/g, " ").slice(0, 500),
    image: abs(portrait),
    worksFor: { "@id": ORG_ID },
    knowsAbout: (leader.territory || "").split(/\s*[-–,]\s*/).filter(Boolean),
    inLanguage: lang,
  });
}

// Pagina editoriale (Chi siamo): AboutPage legata all'organizzazione
export function aboutPageNode({ title, description, path = "/chi-siamo", lang = "it" }) {
  const url = `${SITE_URL}${langPrefix(lang)}${path}`;
  return clean({
    "@type": "AboutPage",
    "@id": `${url}#page`,
    url,
    name: title,
    description: (description || "").replace(/\s+/g, " ").slice(0, 500),
    inLanguage: lang,
    isPartOf: { "@id": `${SITE_URL}/#website` },
    about: { "@id": ORG_ID },
    publisher: { "@id": ORG_ID },
  });
}

// FAQ: FAQPage con domande e risposte (usato anche dagli assistenti AI)
export function faqPageNode({ faqs = [], title, path = "/faq", lang = "it" }) {
  const url = `${SITE_URL}${langPrefix(lang)}${path}`;
  return clean({
    "@type": "FAQPage",
    "@id": `${url}#faq`,
    url,
    name: title,
    inLanguage: lang,
    isPartOf: { "@id": `${SITE_URL}/#website` },
    publisher: { "@id": ORG_ID },
    mainEntity: faqs
      .filter((f) => f.question && f.answer)
      .map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answer.replace(/\s+/g, " ") },
      })),
  });
}

// Elenchi: ItemList con i link alle singole pagine
export function itemListNode({ id, items, lang = "it" }) {
  return {
    "@type": "ItemList",
    "@id": id,
    numberOfItems: items.length,
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      url: SITE_URL + langPrefix(lang) + it.path,
    })),
  };
}

export { ORG_ID };
